import React from 'react'
import { Label, FormFeedback } from 'reactstrap'
import { Controller, Control, FieldValues, FieldErrors } from 'react-hook-form'
import ICSToolTip from '../ICSToolTip/ICSToolTip'
import { ButtonGroup } from 'devextreme-react/button-group'
import PropTypes from 'prop-types'

const ICSButtonGroup = ({
  name,
  id,
  control,
  errors,
  label,
  items,
  helpText,
  keyExpr = 'text',
  defaultSelectedKeys = [items?.[0][keyExpr]],
  defaultValues = defaultSelectedKeys,
  stylingMode = 'contained',
  selectionMode = 'single',
}: ICSButtonGroupProps) => {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValues}
      render={({ field: { onChange, name, ref } }) => {
        const onValueChanged = e => {
          // if (selectionMode === 'single') {
          onChange([e.addedItems[0][keyExpr]])
          // } else {
          //   let result
          //   if (e.removedItems[0]) {
          //     value.splice(value.indexOf(e.removedItems[0][keyExpr]), 1)
          //     result = value
          //   } else {
          //     result = [...value, e.addedItems[0][keyExpr]]
          //   }

          //   onChange(result)
          // }
        }
        return (
          <>
            <Label for={id} className='input-label'>
              {label}
              {helpText && (
                <ICSToolTip
                  id={`${name}ToolTip`}
                  text={helpText}
                  target={`#${name}ToolTip`}
                />
              )}
            </Label>
            <ButtonGroup
              id={id}
              defaultSelectedItemKeys={defaultSelectedKeys}
              items={items}
              keyExpr={keyExpr}
              onSelectionChanged={onValueChanged}
              ref={ref}
              stylingMode={stylingMode}
              selectionMode={selectionMode}
            />
            <FormFeedback
              valid={errors?.[name] ? false : true}
              role='alert'
              id={`${name}Error`}
            >
              {errors?.[name]?.message}
            </FormFeedback>
          </>
        )
      }}
    />
  )
}

export default ICSButtonGroup

type ICSButtonGroupProps<TFieldValues extends FieldValues = FieldValues> = {
  id?: string
  name: string
  control: Control<TFieldValues>
  errors: FieldErrors<TFieldValues>
  label: string
  items: Array<ICSButtonGroupItem>
  helpText?: string
  defaultSelectedKeys?: string[]
  defaultValues?: string[]
  keyExpr?: string
  stylingMode?: 'text' | 'outlined' | 'contained'
  selectionMode?: 'single'
}

type ICSButtonGroupItem = {
  text?: string
  type?: 'danger' | 'default' | 'normal' | 'success'
  hint?: string
  icon?: string
}

const validateDefaults = (propValue, key) => {
  if (propValue[key]) {
    const itemValues = propValue.items.reduce((acc, curr) => {
      acc.push(curr[propValue.keyExpr || 'text'])
      return acc
    }, [])

    if (
      key === 'defaultValues' &&
      propValue.defaultSelectedKeys &&
      propValue[key].length !== propValue.defaultSelectedKeys.length
    ) {
      throw new Error(
        'Specified number of default values does not align with the number of default selected keys'
      )
    }

    propValue[key].forEach(defaultValue => {
      if (!itemValues.includes(defaultValue)) {
        throw new Error(
          `Specified default value: '${defaultValue}' must match the keyExpr of an item passed to the ICSButtonGroup`
        )
      }
    })
  }
}

ICSButtonGroup.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      type: PropTypes.oneOf(['danger', 'default', 'normal', 'success']),
      hint: PropTypes.string,
      icon: PropTypes.string,
    })
  ),
  keyExpr: propValue => {
    propValue.items.forEach(item => {
      if (typeof item[propValue.keyExpr] !== 'string') {
        return new Error(
          `All items passed to the ICSButtonGroup must contain a valid string for the specified keyExpr field: ${propValue.keyExpr}`
        )
      }
    })
  },
  defaultSelectedKeys: validateDefaults,
  defaultValues: validateDefaults,
  selectionMode: propValue => {
    const selectionType = propValue.selectionMode
      ? propValue.selectionMode
      : 'single'
    const keys = propValue.defaultSelectedKeys || []
    const values = propValue.defaultValues || []

    if (selectionType === 'single' && (keys.length > 1 || values.length > 1)) {
      throw new Error(
        'Multiple default keys or values cannot be specified with selectionMode set to "single"'
      )
    }
  },
}
